// Standalone field for editing a JSON Schema Draft-07 decision form definition.
// Validates the raw text on every change and surfaces errors inline so the
// parent form can gate the submit button when the schema is malformed.
import { validateDraft07Subset, type ValidationError } from "@/components/shared/validate-draft-07-subset";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

interface CheckpointDecisionFormFieldProps {
  label: string;
  value: string;
  onChange: (raw: string) => void;
  hint?: string;
}

export function CheckpointDecisionFormField({
  label,
  value,
  onChange,
  hint,
}: CheckpointDecisionFormFieldProps) {
  const errors: ValidationError[] = value.trim()
    ? validateDraft07Subset(value)
    : [];

  return (
    <div className="flex flex-col gap-1">
      <label className="mb-0.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
        {label}
      </label>
      <HighlightedCodeEditor
        value={value}
        onChange={(e) => onChange(e.target.value)}
        language="json"
        rows={6}
        placeholder={
          '{\n  "type": "object",\n  "properties": {\n    "note": { "type": "string" }\n  }\n}'
        }
        minHeight={150}
        invalid={errors.length > 0}
      />
      {hint && errors.length === 0 && (
        <p className="text-xs text-[var(--muted-foreground)]">{hint}</p>
      )}
      {errors.length > 0 && (
        <ul className="mt-0.5 list-inside list-disc text-xs text-[var(--destructive)]">
          {errors.map((e, i) => (
            <li key={i}>
              {e.path}: {e.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Returns true when the raw schema string is empty or passes Draft-07 subset validation. */
export function isDecisionFormValid(raw: string): boolean {
  if (!raw.trim()) return true;
  return validateDraft07Subset(raw).length === 0;
}

export default CheckpointDecisionFormField;
